import React, { useState, useEffect, useCallback, useRef } from "react";
import { ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import type { MemberReportFilters as MemberReportFilterValues } from "../../hooks/useMemberReport";

interface MemberReportFiltersProps {
  filters: MemberReportFilterValues;
  /** What Clear restores; normally `defaultDateRange()`. */
  defaultFilters: MemberReportFilterValues;
  /** Which date the range filters on, e.g. "Date Issued". */
  dateLabel: string;
  isExporting: boolean;
  onExportCsv: () => void;
  onFilterChange: (
    key: keyof MemberReportFilterValues,
    value: string | undefined,
  ) => void;
}

const MemberReportFilters: React.FC<MemberReportFiltersProps> = ({
  filters,
  defaultFilters,
  dateLabel,
  isExporting,
  onExportCsv,
  onFilterChange,
}) => {
  const [searchTerm, setSearchTerm] = useState(filters.searchTerm ?? "");
  const [dateFrom, setDateFrom] = useState(filters.dateFrom ?? "");
  const [dateTo, setDateTo] = useState(filters.dateTo ?? "");

  const debouncedSearchTerm = useDebouncedValue(searchTerm, 500);
  const isMounted = useRef(false);

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }
    onFilterChange("searchTerm", debouncedSearchTerm || undefined);
  }, [debouncedSearchTerm, onFilterChange]);

  const handleDateFromChange = (value: string) => {
    setDateFrom(value);
    onFilterChange("dateFrom", value || undefined);
  };

  const handleDateToChange = (value: string) => {
    setDateTo(value);
    onFilterChange("dateTo", value || undefined);
  };

  const handleClearFilters = useCallback(() => {
    setSearchTerm("");
    setDateFrom(defaultFilters.dateFrom ?? "");
    setDateTo(defaultFilters.dateTo ?? "");
    onFilterChange("searchTerm", undefined);
    onFilterChange("dateFrom", defaultFilters.dateFrom);
    onFilterChange("dateTo", defaultFilters.dateTo);
  }, [defaultFilters, onFilterChange]);

  const hasActiveFilters =
    searchTerm !== "" ||
    dateFrom !== (defaultFilters.dateFrom ?? "") ||
    dateTo !== (defaultFilters.dateTo ?? "");

  return (
    <div className="mb-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        <div>
          <Label htmlFor="searchTerm">Search</Label>
          <Input
            className="mt-1.5 bg-white dark:bg-slate-800"
            id="searchTerm"
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search..."
            type="text"
            value={searchTerm}
          />
        </div>

        <div>
          <Label htmlFor="dateFrom">{dateLabel} From</Label>
          <Input
            className="mt-1.5 bg-white dark:bg-slate-800"
            id="dateFrom"
            onChange={(e) => handleDateFromChange(e.target.value)}
            type="date"
            value={dateFrom}
          />
        </div>

        <div>
          <Label htmlFor="dateTo">{dateLabel} To</Label>
          <Input
            className="mt-1.5 bg-white dark:bg-slate-800"
            id="dateTo"
            onChange={(e) => handleDateToChange(e.target.value)}
            type="date"
            value={dateTo}
          />
        </div>

        <div className="flex items-end gap-2">
          <Button
            className="border-gray-300 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
            disabled={!hasActiveFilters}
            onClick={handleClearFilters}
            variant="outline"
          >
            Clear Filters
          </Button>
          <Button
            className="bg-green-600 hover:bg-green-700"
            disabled={isExporting}
            onClick={onExportCsv}
          >
            <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
            {isExporting ? "Exporting..." : "Export CSV"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MemberReportFilters;
